/**
 * Hash-based allowlist of preset themes that are allowed to bypass the pro
 * plan check for theming.
 *
 * Instead of hashing the full muiTheme (which is fragile to default-filling
 * and format transformations in the ThemeEditor pipeline), we extract a
 * fingerprint from 6 identity-defining properties:
 *
 *   primary.main | secondary.main | header.main | background.page | fontFamily | borderRadius
 *
 * These uniquely identify each preset and are never transformed by the
 * extractFormValues → cleanThemeForOutput round-trip.
 *
 * Keep this list in sync with the presets defined in the React repo at
 * `timum_react_components/src/components/ThemeEditor/themePresets.js`.
 * Whenever a preset's palette.primary.main, palette.secondary.main,
 * palette.header.main, palette.background.page, typography.fontFamily or
 * shape.borderRadius changes, recompute its hash via
 * `fnv1a32(getThemeFingerprint(preset.theme))` below and update the entry.
 */

// ---------------------------------------------------------------------------
// FNV-1a 32-bit hashes of preset fingerprints
// ---------------------------------------------------------------------------

// Verified against the shipping presets in
// `timum_react_components/src/components/ThemeEditor/themePresets.js` — each
// entry below is `fnv1a32(getThemeFingerprint(THEME_PRESETS[key].theme))`
// recomputed from that file. The font named in each comment is the FIRST family
// in the preset's stack; the CLAUDE.md preset table names a later fallback from
// the same stack, so the two describe the same preset.
export const PRESET_FINGERPRINT_HASHES = new Set([
  '0d2d4cee', // mappe      — sorgfältig & seriös   (Source Serif 4, 4px radius)
  'c8f369ff', // werkbank   — direkt & robust       (Oswald, 0 radius)
  'ae84e3c6', // feierabend — einladend             (Spectral, 10px radius)
  '2f906ccd', // pigment    — mit Handschrift       (Quicksand, 16px radius)
  '95d1c745', // kadenz     — klar & in Bewegung    (system-ui, 6px radius)
  'f79d3fae', // nocturne   — dunkel & modern       (Inter/system, 14px radius)
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract the identity fingerprint from a muiTheme object.
 * Concatenates the 6 properties that define a preset's visual identity.
 *
 * borderRadius uses explicit-unset handling (`== null ? '' : String(...)`) so
 * an unset radius fingerprints as '' rather than colliding with an explicit
 * `borderRadius: 0`. The old `borderRadius || 0` mapped both cases to '0'.
 */
export function getThemeFingerprint(muiTheme) {
  const p = muiTheme.palette || {};
  const shape = muiTheme.shape || {};
  return [
    (p.primary || {}).main || '',
    (p.secondary || {}).main || '',
    (p.header || {}).main || '',
    (p.background || {}).page || (p.background || {}).default || '',
    (muiTheme.typography || {}).fontFamily || '',
    shape.borderRadius == null ? '' : String(shape.borderRadius),
  ].join('|');
}

/**
 * FNV-1a 32-bit hash – fast, synchronous, collision-resistant enough for a
 * small allowlist.
 *
 * The hex output is zero-padded to a fixed width of 8 characters. Without
 * padding, any hash whose top hex digits are zero would render shorter (e.g.
 * `0x00abcdef` → 'abcdef'), so a padded allowlist entry could never match the
 * unpadded runtime hash — the F2 bug class.
 *
 * @param {string} str
 * @returns {string} Hex-encoded 32-bit hash, always 8 lowercase hex chars
 */
export function fnv1a32(str) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Check whether a muiTheme object matches one of the preset themes.
 *
 * Extracts a fingerprint from 6 identity properties and checks the FNV-1a
 * hash against the allowlist. This is robust against default-filling and
 * format transformations that occur in the ThemeEditor pipeline.
 *
 * @param {object} muiTheme - The theme object to check
 * @returns {boolean} True if the theme matches a preset
 */
export function isPresetThemeSync(muiTheme) {
  if (!muiTheme || typeof muiTheme !== 'object') return false;
  return PRESET_FINGERPRINT_HASHES.has(fnv1a32(getThemeFingerprint(muiTheme)));
}
